import { useState, useEffect, useCallback } from 'react';
import './settings-responsive.css';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Lock, Eye, EyeOff, Pencil, Check, X, AlertTriangle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import useJobConfig from '../hooks/useJobConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_DOTS = {
  slate: 'bg-slate-500', red: 'bg-red-500', orange: 'bg-orange-500',
  amber: 'bg-amber-500', yellow: 'bg-yellow-500', lime: 'bg-lime-500',
  green: 'bg-green-500', emerald: 'bg-emerald-500', teal: 'bg-teal-500',
  cyan: 'bg-cyan-500', sky: 'bg-sky-500', blue: 'bg-blue-500',
  indigo: 'bg-indigo-500', violet: 'bg-violet-500', purple: 'bg-purple-500',
  pink: 'bg-pink-500', rose: 'bg-rose-500',
};

function ColorPicker({ value, onChange, allowed }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid="color-picker">
      {allowed.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          aria-label={c}
          title={c}
          className={`h-6 w-6 rounded-full ${COLOR_DOTS[c] || 'bg-slate-500'} ${
            value === c ? 'ring-2 ring-offset-1 ring-slate-900' : ''
          }`}
          data-testid={`color-${c}`}
        />
      ))}
    </div>
  );
}

export default function JobConfigSettings() {
  const navigate = useNavigate();
  const { refresh: refreshSharedConfig } = useJobConfig();
  const [data, setData] = useState(null);
  const [newStatus, setNewStatus] = useState({ label: '', color: 'slate' });
  const [newType, setNewType] = useState('');
  const [editing, setEditing] = useState(null); // { kind:'status'|'type', value, label, color? }
  const [orphans, setOrphans] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkType, setBulkType] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const [cfgRes, orphRes] = await Promise.all([
        axios.get(`${API}/job-config`),
        axios.get(`${API}/orphaned-jobs`),
      ]);
      setData(cfgRes.data);
      setOrphans(orphRes.data);
      // Settings is the ONLY place job-types/statuses are mutated. Push the
      // fresh config back into the shared context so the New-Job dropdown
      // (and every status badge) updates without requiring a re-login.
      refreshSharedConfig();
    } catch {
      toast.error('Failed to load job config');
    }
  }, [refreshSharedConfig]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const isSilver = data?.is_silver_tier;
  const allowedColors = data?.allowed_colors || [];

  const addStatus = async (e) => {
    e.preventDefault();
    if (!newStatus.label.trim()) return;
    try {
      await axios.post(`${API}/settings/job-statuses`, newStatus);
      toast.success('Status added');
      setNewStatus({ label: '', color: 'slate' });
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const addType = async (e) => {
    e.preventDefault();
    if (!newType.trim()) return;
    try {
      await axios.post(`${API}/settings/job-types`, { label: newType });
      toast.success('Type added');
      setNewType('');
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const toggleStatusHidden = async (s) => {
    try {
      const res = await axios.patch(`${API}/settings/job-statuses/${s.value}/visibility`, { hidden: !s.hidden });
      if (res.data.orphaned_jobs > 0) {
        toast.warning(`Hidden — ${res.data.orphaned_jobs} existing job(s) still use this status. View them under "Orphaned Jobs" below.`);
      }
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const toggleTypeHidden = async (t) => {
    try {
      const res = await axios.patch(`${API}/settings/job-types/${t.value}/visibility`, { hidden: !t.hidden });
      if (res.data.orphaned_jobs > 0) {
        toast.warning(`Hidden — ${res.data.orphaned_jobs} existing job(s) still use this type.`);
      }
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteStatus = async (s) => {
    if (!window.confirm(`Delete status "${s.label}"? Existing jobs using it will appear under Orphaned Jobs.`)) return;
    try {
      const res = await axios.delete(`${API}/settings/job-statuses/${s.value}`);
      if (res.data.orphaned_jobs > 0) {
        toast.warning(`Deleted — ${res.data.orphaned_jobs} job(s) need a new status assigned.`);
      } else {
        toast.success('Deleted');
      }
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteType = async (t) => {
    if (!window.confirm(`Delete type "${t.label}"?`)) return;
    try {
      const res = await axios.delete(`${API}/settings/job-types/${t.value}`);
      if (res.data.orphaned_jobs > 0) {
        toast.warning(`Deleted — ${res.data.orphaned_jobs} job(s) need a new type assigned.`);
      } else {
        toast.success('Deleted');
      }
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      if (editing.kind === 'status') {
        await axios.patch(`${API}/settings/job-statuses/${editing.value}`, {
          label: editing.label,
          color: editing.color,
        });
      } else {
        await axios.patch(`${API}/settings/job-types/${editing.value}`, { label: editing.label });
      }
      toast.success('Updated');
      setEditing(null);
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (!data) return null;

  const activeStatuses = (data.statuses || []).filter((s) => !s.hidden);
  const activeTypes = (data.types || []).filter((t) => !t.hidden);
  const orphanJobs = orphans?.jobs || [];
  const allSelected = orphanJobs.length > 0 && orphanJobs.every((j) => selectedIds.has(j.id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orphanJobs.map((j) => j.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkStatus('');
    setBulkType('');
  };

  const applyBulk = async () => {
    if (selectedIds.size === 0) return;
    if (!bulkStatus && !bulkType) {
      toast.error('Pick a new status, a new type, or both.');
      return;
    }
    const payload = { job_ids: Array.from(selectedIds) };
    if (bulkStatus) payload.status = bulkStatus;
    if (bulkType) payload.job_type = bulkType;
    setBulkApplying(true);
    try {
      const res = await axios.post(`${API}/jobs/bulk-update`, payload);
      toast.success(`Updated ${res.data.modified} of ${res.data.matched} job(s).`);
      clearSelection();
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to bulk update');
    } finally {
      setBulkApplying(false);
    }
  };

  return (
    <>
      {/* Statuses Card */}
      <Card className="p-6 bg-white border border-slate-200 shadow-sm" data-testid="job-statuses-card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              Job Statuses
              <span className={`text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1 ${
                isSilver ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'
              }`}>
                <Sparkles size={12} /> {data.is_gold_tier ? 'Gold' : 'Silver+'}
              </span>
            </h4>
            <p className="text-sm text-slate-500 mt-1">
              Hide defaults you don&apos;t use and add your own custom statuses.
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-4" data-testid="status-list">
          {data.statuses.map((s) => (
            <div
              key={s.value}
              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border ${
                s.hidden ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'
              }`}
              data-testid={`status-row-${s.value}`}
            >
              {editing?.kind === 'status' && editing.value === s.value ? (
                <div className="flex-1 flex items-center gap-2">
                  <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
                  <ColorPicker value={editing.color} onChange={(c) => setEditing({ ...editing, color: c })} allowed={allowedColors} />
                  <Button size="sm" onClick={saveEdit}><Check size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}><X size={14} /></Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 flex-1">
                    <span className={`inline-block h-3 w-3 rounded-full ${COLOR_DOTS[s.color] || 'bg-slate-500'}`} />
                    <span className="font-medium text-slate-900">{s.label}</span>
                    {s.is_default && (
                      <span className="text-xs text-slate-500 uppercase tracking-wider">default</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {!s.is_default && isSilver && (
                      <Button size="sm" variant="outline" onClick={() => setEditing({ kind: 'status', value: s.value, label: s.label, color: s.color })} data-testid={`edit-status-${s.value}`}>
                        <Pencil size={12} />
                      </Button>
                    )}
                    {s.is_default && isSilver && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleStatusHidden(s)}
                        data-testid={`toggle-status-${s.value}`}
                        title={s.hidden ? 'Unhide' : 'Hide'}
                      >
                        {s.hidden ? <Eye size={12} /> : <EyeOff size={12} />}
                      </Button>
                    )}
                    {!s.is_default && isSilver && (
                      <Button size="sm" variant="outline" onClick={() => deleteStatus(s)} className="text-red-600" data-testid={`delete-status-${s.value}`}>
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {isSilver ? (
          <form onSubmit={addStatus} className="flex gap-2 items-end flex-wrap pt-3 border-t border-slate-200" data-testid="add-status-form">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500">New status label</label>
              <Input
                placeholder="e.g. QC Review"
                value={newStatus.label}
                onChange={(e) => setNewStatus({ ...newStatus, label: e.target.value })}
                data-testid="new-status-label"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500 block mb-1">Color</label>
              <ColorPicker
                value={newStatus.color}
                onChange={(c) => setNewStatus({ ...newStatus, color: c })}
                allowed={allowedColors}
              />
            </div>
            <Button type="submit" className="bg-slate-900" data-testid="add-status-btn">
              <Plus size={16} className="mr-1" /> Add
            </Button>
          </form>
        ) : (
          <div className="pt-3 border-t border-slate-200 bg-amber-50 -mx-6 -mb-6 px-6 py-4 rounded-b-md flex items-start gap-2">
            <Lock size={16} className="text-amber-700 mt-0.5" />
            <p className="text-sm text-amber-900">
              Custom job statuses are a <strong>Silver/Gold tier</strong> feature.{' '}
              <a href="/billing" className="underline font-semibold">Upgrade your plan</a> to unlock.
            </p>
          </div>
        )}
      </Card>

      {/* Types Card */}
      <Card className="p-6 bg-white border border-slate-200 shadow-sm" data-testid="job-types-card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              Job Types
              <span className={`text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1 ${
                isSilver ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'
              }`}>
                <Sparkles size={12} /> {data.is_gold_tier ? 'Gold' : 'Silver+'}
              </span>
            </h4>
            <p className="text-sm text-slate-500 mt-1">
              Define the kinds of work your shop handles (Repair, Inspection, etc).
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-4" data-testid="type-list">
          {data.types.map((t) => (
            <div
              key={t.value}
              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border ${
                t.hidden ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'
              }`}
              data-testid={`type-row-${t.value}`}
            >
              {editing?.kind === 'type' && editing.value === t.value ? (
                <div className="flex-1 flex items-center gap-2">
                  <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
                  <Button size="sm" onClick={saveEdit}><Check size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}><X size={14} /></Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 flex-1">
                    <span className="font-medium text-slate-900">{t.label}</span>
                    {t.is_default && (
                      <span className="text-xs text-slate-500 uppercase tracking-wider">default</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {!t.is_default && isSilver && (
                      <Button size="sm" variant="outline" onClick={() => setEditing({ kind: 'type', value: t.value, label: t.label })} data-testid={`edit-type-${t.value}`}>
                        <Pencil size={12} />
                      </Button>
                    )}
                    {t.is_default && isSilver && (
                      <Button size="sm" variant="outline" onClick={() => toggleTypeHidden(t)} data-testid={`toggle-type-${t.value}`} title={t.hidden ? 'Unhide' : 'Hide'}>
                        {t.hidden ? <Eye size={12} /> : <EyeOff size={12} />}
                      </Button>
                    )}
                    {!t.is_default && isSilver && (
                      <Button size="sm" variant="outline" onClick={() => deleteType(t)} className="text-red-600" data-testid={`delete-type-${t.value}`}>
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {isSilver && (
          <form onSubmit={addType} className="flex gap-2 pt-3 border-t border-slate-200" data-testid="add-type-form">
            <Input
              placeholder="e.g. Inspection"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              data-testid="new-type-label"
            />
            <Button type="submit" className="bg-slate-900" data-testid="add-type-btn">
              <Plus size={16} className="mr-1" /> Add
            </Button>
          </form>
        )}
      </Card>

      {/* Orphaned Jobs Card */}
      {orphans && orphans.count > 0 && (
        <Card className="p-6 bg-white border border-amber-200 shadow-sm" data-testid="orphaned-jobs-card">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle size={20} className="text-amber-700 mt-0.5" />
            <div>
              <h4 className="text-lg font-bold text-slate-900">Orphaned Jobs ({orphans.count})</h4>
              <p className="text-sm text-slate-600">
                These jobs reference a status or type that&apos;s been removed or hidden. Open each job to assign a new status/type, or check several and use the bulk re-assign tool below.
              </p>
            </div>
          </div>

          {/* Bulk Actions Toolbar */}
          {selectedIds.size > 0 && (
            <div
              className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
              data-testid="bulk-actions-toolbar"
            >
              <span className="text-sm font-semibold text-amber-900" data-testid="bulk-selected-count">
                {selectedIds.size} selected
              </span>
              <div className="flex items-center gap-1">
                <label className="text-xs text-slate-600">New status</label>
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  className="h-8 rounded border border-slate-300 bg-white px-2 text-sm"
                  data-testid="bulk-status-select"
                >
                  <option value="">— keep —</option>
                  {activeStatuses.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-slate-600">New type</label>
                <select
                  value={bulkType}
                  onChange={(e) => setBulkType(e.target.value)}
                  className="h-8 rounded border border-slate-300 bg-white px-2 text-sm"
                  data-testid="bulk-type-select"
                >
                  <option value="">— keep —</option>
                  {activeTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <Button
                size="sm"
                onClick={applyBulk}
                disabled={bulkApplying || (!bulkStatus && !bulkType)}
                data-testid="bulk-apply-btn"
              >
                {bulkApplying ? 'Applying…' : 'Apply'}
              </Button>
              <Button size="sm" variant="outline" onClick={clearSelection} data-testid="bulk-clear-btn">
                Clear
              </Button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="orphan-jobs-table w-full text-sm" data-testid="orphan-jobs-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                      data-testid="orphan-select-all"
                      aria-label="Select all orphaned jobs"
                    />
                    <span className="ml-2 md:hidden" data-testid="orphan-select-all-label">Select all</span>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Job</th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Customer</th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Bad Status</th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Bad Type</th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {orphanJobs.map((j) => (
                  <tr key={j.id} data-testid={`orphan-${j.id}`} className={selectedIds.has(j.id) ? 'bg-amber-50' : ''}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(j.id)}
                        onChange={() => toggleOne(j.id)}
                        data-testid={`orphan-select-${j.id}`}
                        aria-label={`Select job ${j.job_number}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900">#{j.job_number}</td>
                    <td className="px-3 py-2 text-slate-700">{j.customer_name || '-'}</td>
                    <td className="px-3 py-2" data-label="Bad status">
                      {j.orphaned_status ? (
                        <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">{j.status}</span>
                      ) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-3 py-2" data-label="Bad type">
                      {j.orphaned_type ? (
                        <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">{j.job_type}</span>
                      ) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`open-orphan-${j.id}`}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
